import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Icon from "components/ShellV2/Icon";

/* Checkbox multi-select dropdown (port of the design's MultiSelect). options:
   [{value, label, distinct?}]. `selected` is an array of values. */
export default function MultiSelect({
  options,
  selected,
  onChange,
  selectAll,
  selectAllLabel,
  placeholder,
  countNoun,
}) {
  const { t } = useTranslation(["common"]);
  const placeholderText = placeholder ?? t("select_placeholder");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const total = options.length;
  const allSel = selected.length === total && total > 0;
  const toggle = (v) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const toggleAll = () => onChange(allSel ? [] : options.map((o) => o.value));

  return (
    <div className="rec-ms" ref={ref}>
      <button
        type="button"
        className={`rec-ms-control${open ? " open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected.length ? "" : "rec-ms-ph"}>
          {selected.length > 0 ? `${selected.length} / ${total} ${countNoun}` : placeholderText}
        </span>
        <Icon name="expand_more" />
      </button>
      {open && (
        <div className="rec-ms-menu">
          {selectAll && (
            <button type="button" className="rec-ms-opt ms-all" onClick={toggleAll}>
              <span className={`rec-ms-check${allSel ? " on" : ""}`}>
                {allSel && <Icon name="check" style={{ fontSize: 13 }} />}
              </span>
              <span className="rec-ms-lbl">{selectAllLabel ?? t("selectall")}</span>
            </button>
          )}
          {options.map((o) => {
            const sel = selected.includes(o.value);
            return (
              <button
                type="button"
                key={o.value}
                className={`rec-ms-opt${o.distinct ? " distinct" : ""}`}
                onClick={() => toggle(o.value)}
              >
                <span className={`rec-ms-check${sel ? " on" : ""}`}>
                  {sel && <Icon name="check" style={{ fontSize: 13 }} />}
                </span>
                <span className="rec-ms-lbl">{o.label}</span>
                {o.distinct && <Icon name="warning" className="rec-ms-warn" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node,
      distinct: PropTypes.bool,
    })
  ).isRequired,
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onChange: PropTypes.func.isRequired,
  selectAll: PropTypes.bool,
  selectAllLabel: PropTypes.node,
  placeholder: PropTypes.node,
  countNoun: PropTypes.node,
};

MultiSelect.defaultProps = {
  selectAll: false,
  selectAllLabel: undefined,
  placeholder: undefined,
  countNoun: "",
};
